import {
  Box,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Theme,
  useMediaQuery,
} from "@mui/material";
import { SxProps } from "@mui/system";
import { GLOBAL_API_PROP_NAME } from "@sinch/core/config/globals";
import { PositionStatusEnum } from "@sinch/core/entities/serverEnums";
import {
  MdsCircleNotifications,
  MdsEditXs,
  MdsMoreVert,
  MdsStackedEmail,
  SinchMessageQuestionOutline,
} from "@sinch/icons";
import { MessageDialogProps } from "@sinch/views/MessageDialog/MessageDialog";
import { isEmpty } from "ramda";
import React, { ElementType, FC, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon, MdsIconProps } from "@sinch/components";
import {
  PositionShiftPanelDetailFragment,
  ShiftPanelDetailFragment,
} from "@sinch/components/application/ShiftDetailPanel/queries.types";
import { useViewProps } from "@sinch/components/providers/ViewPropsProvider";
import { lazyWithRetry } from "@sinch/utils/lazyWithRetry";

const MessageDialog = lazyWithRetry(() =>
  import("@sinch/views/MessageDialog/MessageDialog").then((mod) => ({ default: mod.MessageDialog }))
);

const ClientReminderDialog = lazyWithRetry(() =>
  import("@sinch/views/ClientReminderDialog/ClientReminderDialog").then((mod) => ({
    default: mod.ClientReminderDialog,
  }))
);

export interface ActionMenuItem {
  /**
   * Title for menu or tooltip
   */
  title: string;
  /**
   * Click event on icon or menu item
   */
  href: string;
  /**
   * Icon for menu or icon
   */
  icon: ElementType;
  /**
   * Color of icon and item
   */
  color?: string;
}

export interface ActionMenuProps {
  /**
   * Size of icon applied for kebab menu icon and list of icon buttons, but not on icons in menu
   */
  iconSize?: MdsIconProps["fontSize"];
  /**
   * If true, version for touch is used, if udnefined, check touch device status automatically
   */
  touch?: boolean;
  /**
   * List of item to show
   */
  items: ActionMenuItem[];
  sx?: SxProps<Theme>;
}

export const HeaderActionMenuMobile: FC<ActionMenuProps> = ({ items, sx, iconSize }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box component="span" sx={sx}>
      <IconButton onClick={handleClick}>
        <MdsIcon color="info" fontSize={iconSize} icon={MdsMoreVert} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        onClose={handleClose}
        open={open}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
      >
        {items.map(({ title, color, href, icon }) => (
          <MenuItem key={title} component={Link} href={href} sx={{ minHeight: "unset" }}>
            <ListItemIcon color={color}>
              <MdsIcon color={color as MdsIconProps["color"]} fontSize="small" icon={icon} />
            </ListItemIcon>
            <ListItemText>{title}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

interface ShiftActionMenuProps {
  shift: ShiftPanelDetailFragment;
  disableCollapse?: boolean;
}

export const ShiftActionMenu: FC<ShiftActionMenuProps> = ({ shift, disableCollapse }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const items = [
    {
      title: t("Action.edit"),
      href: decodeURI(`/admin/jobs/builder/${shift.job?.id}#S${shift.id}`),
      icon: MdsEditXs,
      color: "info",
    },
  ];

  if (isMobile && !disableCollapse) {
    return <HeaderActionMenuMobile items={items} />;
  }

  return (
    <Stack alignItems="center" direction="row" spacing={0}>
      <SendMessageMenu items={items} shift={shift} />
    </Stack>
  );
};

export const SendMessageMenu: FC<
  ActionMenuProps & {
    shift: ShiftPanelDetailFragment;
  }
> = ({ sx, iconSize = "small", shift }) => {
  const messageDialogProps = useViewProps<MessageDialogProps>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const isEmptyShift = shift.positions.flatMap((position) => position.attendances).length === 0;
  const isOver = shift.positions.some(
    ({ status }) => status in [PositionStatusEnum.Over, PositionStatusEnum.CbCompleted, PositionStatusEnum.Completed]
  );

  const hasSomeCancelled = React.useMemo(
    () => shift.positions.some((position) => position.cancelled),
    [shift.positions]
  );

  return (
    <Box component="span" sx={sx}>
      <IconButton onClick={handleClick}>
        <MdsIcon color="info" fontSize={iconSize} icon={MdsMoreVert} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        onClose={handleClose}
        open={open}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
      >
        <MessageMenuItem disabled={isEmptyShift} positions={shift.positions} shiftId={shift.id} />
        {hasSomeCancelled && (
          <MessageExceptCancelledMenuItem disabled={isEmptyShift} positions={shift.positions} shiftId={shift.id} />
        )}
        <ConfirmationRequestMenuItem disabled={isEmptyShift && !isOver} positions={shift.positions} />
        {hasSomeCancelled && (
          <ConfirmationRequestExceptCancelledMenuItem disabled={isEmptyShift && !isOver} positions={shift.positions} />
        )}
        <ClientReminderMenuItem disabled={isEmptyShift && !isOver} shiftId={shift.id} />
      </Menu>
      <Suspense>
        <ClientReminderDialog />
        <MessageDialog {...messageDialogProps} />
      </Suspense>
    </Box>
  );
};

// MenuItem components
interface MessageMenuItemProps {
  disabled: boolean;
  positions: PositionShiftPanelDetailFragment[];
  shiftId: string;
}

const MessageMenuItem: FC<MessageMenuItemProps> = ({ disabled, positions, shiftId }) => {
  const { t } = useTranslation();

  const recipientsIds = React.useMemo(
    () => positions.flatMap((position) => position.attendances.map((attendance) => attendance.worker.id)),
    [positions]
  );

  return (
    <MenuItem
      disabled={disabled}
      onClick={() => {
        window[GLOBAL_API_PROP_NAME].MessageDialog.openMessageDialog({
          recipientsIds: recipientsIds,
          relatedObject: "shift",
          relatedObjectId: shiftId,
        });
      }}
      sx={{ minHeight: "unset" }}
    >
      <ListItemIcon color="info">
        <MdsIcon fontSize="small" icon={MdsStackedEmail} />
      </ListItemIcon>
      <ListItemText>{t("Action.message")}</ListItemText>
    </MenuItem>
  );
};

const MessageExceptCancelledMenuItem: FC<MessageMenuItemProps> = ({ disabled, positions, shiftId }) => {
  const { t } = useTranslation();
  const uncancelledPositions = positions.filter((position) => !position.cancelled);

  const recipientsIds = React.useMemo(
    () => uncancelledPositions.flatMap((position) => position.attendances.map((attendance) => attendance.worker.id)),
    [uncancelledPositions]
  );

  if (isEmpty(recipientsIds)) {
    return;
  }

  return (
    <MenuItem
      disabled={disabled}
      onClick={() => {
        window[GLOBAL_API_PROP_NAME].MessageDialog.openMessageDialog({
          recipientsIds: recipientsIds,
          relatedObject: "shift",
          relatedObjectId: shiftId,
        });
      }}
      sx={{ minHeight: "unset" }}
    >
      <ListItemIcon color="info">
        <MdsIcon fontSize="small" icon={MdsStackedEmail} />
      </ListItemIcon>
      <ListItemText>{`${t("Action.message")} (${t("Shift.exceptCancelled")})`}</ListItemText>
    </MenuItem>
  );
};

interface ConfirmationRequestMenuItemProps {
  disabled: boolean;
  positions: PositionShiftPanelDetailFragment[];
}

const ConfirmationRequestMenuItem: FC<ConfirmationRequestMenuItemProps> = ({ disabled, positions }) => {
  const { t } = useTranslation();

  const reconfirmationAttendances = React.useMemo(
    () =>
      positions.flatMap((position) =>
        position.attendances.map((attendance) => ({ id: attendance.id, userId: attendance.worker.id }))
      ),
    [positions]
  );

  return (
    <MenuItem
      disabled={disabled}
      onClick={() => {
        window[GLOBAL_API_PROP_NAME].MessageDialog.openReconfirmationDialog({
          attendanceIds: reconfirmationAttendances,
        });
      }}
      sx={{ minHeight: "unset" }}
    >
      <ListItemIcon color="info">
        <MdsIcon fontSize="small" icon={SinchMessageQuestionOutline} />
      </ListItemIcon>
      <ListItemText>{t("Message.confirmationRequest")}</ListItemText>
    </MenuItem>
  );
};

const ConfirmationRequestExceptCancelledMenuItem: FC<ConfirmationRequestMenuItemProps> = ({ disabled, positions }) => {
  const { t } = useTranslation();

  const uncancelledPositions = positions.filter((position) => !position.cancelled);

  const uncancelledReconfirmationAttendances = React.useMemo(
    () =>
      uncancelledPositions.flatMap((position) =>
        position.attendances.map((attendance) => ({ id: attendance.id, userId: attendance.worker.id }))
      ),
    [uncancelledPositions]
  );

  if (isEmpty(uncancelledReconfirmationAttendances)) {
    return;
  }

  return (
    <MenuItem
      disabled={disabled}
      onClick={() => {
        window[GLOBAL_API_PROP_NAME].MessageDialog.openReconfirmationDialog({
          attendanceIds: uncancelledReconfirmationAttendances,
        });
      }}
      sx={{ minHeight: "unset" }}
    >
      <ListItemIcon color="info">
        <MdsIcon fontSize="small" icon={SinchMessageQuestionOutline} />
      </ListItemIcon>
      <ListItemText>{t("Message.confirmationRequest")}</ListItemText>
    </MenuItem>
  );
};

interface ClientReminderMenuItemProps {
  disabled: boolean;
  shiftId: string;
}

const ClientReminderMenuItem: FC<ClientReminderMenuItemProps> = ({ disabled, shiftId }) => {
  const { t } = useTranslation();

  return (
    <MenuItem
      disabled={disabled}
      onClick={() => {
        window[GLOBAL_API_PROP_NAME].ClientReminderDialog.openDialog({
          shiftId: shiftId,
        });
      }}
      sx={{ minHeight: "unset" }}
    >
      <ListItemIcon color="info">
        <MdsIcon fontSize="small" icon={MdsCircleNotifications} />
      </ListItemIcon>
      <ListItemText>{t("Action.manualClientShiftReminder")}</ListItemText>
    </MenuItem>
  );
};
